import { BellRing, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import {
  AlertDraftSummary,
  AlertRefusal,
  AlertRefusedError,
  UserAlert,
  apiAlertQuota,
  apiCreateAlert,
  apiDeleteAlert,
  apiListAlerts,
  apiPauseAlert,
  apiPreviewAlert,
  apiResumeAlert
} from './alertsApi';
import { Timeframe } from './api';

// "My Alerts": describe what you want watched in a sentence, read back what
// will actually be checked, then save it. Mounted from the Anomalies page and
// from the chat, exactly as ReportsDialog is.
//
// Three views, same sheet:
//   list    -> the caller's own alerts, with pause/resume/delete
//   create  -> the sentence box
//   confirm -> the plain-language echo the user agrees to before anything is saved

type Mode =
  | { view: 'list' }
  | { view: 'create' }
  | {
      view: 'confirm';
      draftId: number;
      explanation: { ar: string; en: string };
      summary: AlertDraftSummary;
    };

const TIMEFRAMES: { value: Timeframe; label: string }[] = [
  { value: 'DAY', label: 'Day over day' },
  { value: 'WEEK', label: 'Week over week' },
  { value: 'MONTH', label: 'Month over month' },
  { value: 'YEAR', label: 'Year over year' }
];

/** Arabic if the text contains Arabic. This reads the user's INPUT so the
 *  model knows what language it is parsing; nothing the system says back is
 *  chosen by it — that is always English. */
function isArabic(text: string): boolean {
  return /[؀-ۿ]/.test(text);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fills the sentence box (used by the chat entry point). */
  initialSentence?: string;
}

export default function AlertsDialog({
  open,
  onOpenChange,
  initialSentence
}: Props) {
  const [mode, setMode] = useState<Mode>({ view: 'list' });
  const [alerts, setAlerts] = useState<UserAlert[]>([]);
  const [quota, setQuota] = useState<{ used: number; limit: number } | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | 'save' | 'preview' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<AlertRefusal | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, q] = await Promise.all([
        apiListAlerts(true),
        apiAlertQuota()
      ]);
      setAlerts(list.items);
      setQuota(q);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void load();
    // A sentence handed in from the chat means the user already said what they
    // want — go straight to the box rather than making them find the button.
    setMode(initialSentence?.trim() ? { view: 'create' } : { view: 'list' });
    setRefusal(null);
    setError(null);
  }, [open, initialSentence, load]);

  const atCap = !!quota && quota.used >= quota.limit;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        {mode.view === 'list' ? (
          <AlertList
            alerts={alerts}
            quota={quota}
            loading={loading}
            busy={busy}
            error={error}
            atCap={atCap}
            onNew={() => {
              setRefusal(null);
              setMode({ view: 'create' });
            }}
            onRefresh={() => void load()}
            onToggle={async (alert) => {
              setBusy(alert.id);
              setError(null);
              try {
                if (alert.paused) await apiResumeAlert(alert.id);
                else await apiPauseAlert(alert.id);
                await load();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(null);
              }
            }}
            onDelete={async (alert) => {
              if (
                !window.confirm(
                  `Delete this alert? It will stop running tonight.`
                )
              )
                return;
              setBusy(alert.id);
              setError(null);
              try {
                await apiDeleteAlert(alert.id);
                await load();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(null);
              }
            }}
          />
        ) : mode.view === 'create' ? (
          <CreateView
            initialSentence={initialSentence}
            busy={busy === 'preview'}
            refusal={refusal}
            error={error}
            onBack={() => {
              setRefusal(null);
              setMode({ view: 'list' });
            }}
            onSubmit={async (sentence) => {
              setBusy('preview');
              setError(null);
              setRefusal(null);
              try {
                const pv = await apiPreviewAlert(
                  sentence,
                  isArabic(sentence) ? 'ar' : 'en'
                );
                setMode({
                  view: 'confirm',
                  draftId: pv.draft_id,
                  explanation: pv.explanation,
                  summary: pv.summary
                });
              } catch (e) {
                // A refusal is a translated sentence the user can act on. An
                // error is everything else. They render differently.
                if (e instanceof AlertRefusedError) setRefusal(e.refusal);
                else setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(null);
              }
            }}
          />
        ) : (
          <ConfirmView
            explanation={mode.explanation}
            summary={mode.summary}
            busy={busy === 'save'}
            error={error}
            onBack={() => setMode({ view: 'create' })}
            onSave={async (threshold, timeframe) => {
              setBusy('save');
              setError(null);
              try {
                await apiCreateAlert({
                  draft_id: mode.draftId,
                  threshold_pct: threshold,
                  timeframe
                });
                setMode({ view: 'list' });
                await load();
              } catch (e) {
                if (e instanceof AlertRefusedError) setRefusal(e.refusal);
                else setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(null);
              }
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── List ─────────────────────────────────────────────────────────────────────

interface ListProps {
  alerts: UserAlert[];
  quota: { used: number; limit: number } | null;
  loading: boolean;
  busy: number | 'save' | 'preview' | null;
  error: string | null;
  atCap: boolean;
  onNew: () => void;
  onRefresh: () => void;
  onToggle: (alert: UserAlert) => void;
  onDelete: (alert: UserAlert) => void;
}

function AlertList({
  alerts,
  quota,
  loading,
  busy,
  error,
  atCap,
  onNew,
  onRefresh,
  onToggle,
  onDelete
}: ListProps) {
  return (
    <>
      <SheetHeader>
        <SheetTitle>My alerts</SheetTitle>
        <SheetDescription>
          Things you asked me to watch. Checked every morning.
        </SheetDescription>
      </SheetHeader>

      <div className="mt-4 flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          {quota ? `${quota.used} of ${quota.limit} alerts used` : ' '}
        </span>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw className={`size-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={onNew} disabled={atCap}>
            <Plus className="size-4 mr-2" />
            New alert
          </Button>
        </div>
      </div>

      {atCap ? (
        <p className="mt-2 text-sm text-muted-foreground">
          You&apos;ve used all your alerts. Delete one to make room for another.
        </p>
      ) : null}

      {error ? (
        <Card className="mt-4 border-destructive/50 p-3 text-sm text-destructive">
          {error}
        </Card>
      ) : null}

      <div className="mt-4 space-y-3">
        {loading && alerts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : alerts.length === 0 ? (
          <Card className="p-6 text-center">
            <BellRing className="size-6 mx-auto text-muted-foreground" />
            <p className="mt-3 text-sm font-medium">No alerts yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Describe what you want watched and I&apos;ll check it every
              morning.
            </p>
            <Button className="mt-4" size="sm" onClick={onNew}>
              <Plus className="size-4 mr-2" />
              Create one
            </Button>
          </Card>
        ) : (
          alerts.map((alert) => {
            // English, whatever the alert was written in: this is the system
            // describing the rule it built, not an answer to a question.
            // The Arabic copy is still stored and is the last resort only for
            // rows saved before that rule (2026-09-12).
            const sentence =
              alert.explanation.en ?? alert.explanation.ar ?? '';
            return (
              <Card key={alert.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    {/* The user's own words, then the promise the system made
                        back. dir=auto so Arabic renders right-to-left. */}
                    {alert.source_text ? (
                      <p className="text-sm font-medium break-words" dir="auto">
                        {alert.source_text}
                      </p>
                    ) : null}
                    <p
                      className="mt-1 text-sm text-muted-foreground break-words"
                      dir="auto"
                    >
                      {sentence}
                    </p>
                    {alert.paused ? (
                      <p className="mt-2 text-xs font-medium text-muted-foreground">
                        Paused — not running tonight
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {busy === alert.id ? (
                      <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    ) : (
                      <Switch
                        checked={!alert.paused}
                        onCheckedChange={() => onToggle(alert)}
                        aria-label={
                          alert.paused ? 'Resume alert' : 'Pause alert'
                        }
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(alert)}
                      disabled={busy === alert.id}
                      aria-label="Delete alert"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </>
  );
}

// ── Create ───────────────────────────────────────────────────────────────────

interface CreateProps {
  initialSentence?: string;
  busy: boolean;
  refusal: AlertRefusal | null;
  error: string | null;
  onBack: () => void;
  onSubmit: (sentence: string) => void;
}

function CreateView({
  initialSentence,
  busy,
  refusal,
  error,
  onBack,
  onSubmit
}: CreateProps) {
  const [sentence, setSentence] = useState(initialSentence ?? '');

  return (
    <>
      <SheetHeader>
        <SheetTitle>New alert</SheetTitle>
        <SheetDescription>
          Say what you want watched, in Arabic or English.
        </SheetDescription>
      </SheetHeader>

      <div className="mt-4 space-y-3">
        <Textarea
          value={sentence}
          onChange={(e) => setSentence(e.target.value)}
          placeholder="Tell me if daily sales for the Maadi branch drop more than 20%…"
          rows={4}
          dir="auto"
          maxLength={500}
          autoFocus
        />

        {refusal ? (
          <RefusalCard refusal={refusal} onPick={setSentence} />
        ) : null}

        {error ? (
          <Card className="border-destructive/50 p-3 text-sm text-destructive">
            {error}
          </Card>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onBack} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(sentence)}
            disabled={busy || !sentence.trim()}
          >
            {busy ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
            Check this
          </Button>
        </div>
      </div>
    </>
  );
}

/** A refusal, in English. Never the raw validator text — that is admin-only
 *  and the API withholds it from everyone else. */
function RefusalCard({
  refusal,
  onPick
}: {
  refusal: AlertRefusal;
  onPick: (sentence: string) => void;
}) {
  return (
    <Card className="border-destructive/50 p-3 space-y-2">
      <p className="text-sm" dir="auto">
        {refusal.message_en || refusal.message_ar}
      </p>
      {refusal.choices.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {refusal.choices.map((c) => (
            <button
              key={`${c.column}:${c.value}`}
              type="button"
              dir="auto"
              onClick={() => onPick(c.value)}
              className="rounded-full border px-3 py-1 text-xs hover:bg-accent"
            >
              {c.value}
            </button>
          ))}
        </div>
      ) : null}
      {refusal.suggestions.length > 0 ? (
        <p className="text-xs text-muted-foreground" dir="auto">
          Available: {refusal.suggestions.slice(0, 8).join(', ')}
        </p>
      ) : null}
      {refusal.detail ? (
        <p className="text-xs font-mono text-muted-foreground break-all">
          {refusal.detail}
        </p>
      ) : null}
    </Card>
  );
}

// ── Confirm ──────────────────────────────────────────────────────────────────

interface ConfirmProps {
  explanation: { ar: string; en: string };
  summary: AlertDraftSummary;
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onSave: (
    threshold: number | undefined,
    timeframe: Timeframe | undefined
  ) => void;
}

function ConfirmView({
  explanation,
  summary,
  busy,
  error,
  onBack,
  onSave
}: ConfirmProps) {
  const [threshold, setThreshold] = useState<number | null>(
    summary.threshold_pct
  );
  const [timeframe, setTimeframe] = useState<Timeframe>(summary.timeframe);
  const isZScore = summary.rule_type === 'SAME_WEEKDAY_ZSCORE';

  // The echo is rendered from the SAVED rule, not from what the user typed, so
  // a wrong reading shows up here rather than three weeks later. It is English
  // whatever the user wrote, like every other thing the system says.

  return (
    <>
      <SheetHeader>
        <SheetTitle>Is this right?</SheetTitle>
        <SheetDescription>Nothing is saved until you confirm.</SheetDescription>
      </SheetHeader>

      <div className="mt-4 space-y-4">
        <Card className="p-4">
          <p className="text-base leading-relaxed" dir="auto">
            {explanation.en || explanation.ar}
          </p>
        </Card>

        {summary.resolved_filters.length > 0 ? (
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              Matched in your data
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {summary.resolved_filters.map((f) => (
                <span
                  key={`${f.column}:${f.value}`}
                  dir="auto"
                  className="rounded-full bg-muted px-3 py-1 text-xs"
                >
                  {f.column}: {f.value}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {!isZScore ? (
          <>
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Compare
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {TIMEFRAMES.map((tf) => (
                  <button
                    key={tf.value}
                    type="button"
                    onClick={() => setTimeframe(tf.value)}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      timeframe === tf.value
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    }`}
                  >
                    {tf.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label
                htmlFor="alert-threshold"
                className="text-xs font-medium text-muted-foreground"
              >
                Tell me when it changes by at least
              </label>
              <div className="mt-2 flex items-center gap-2">
                <input
                  id="alert-threshold"
                  type="number"
                  min={1}
                  max={1000}
                  value={threshold ?? ''}
                  onChange={(e) =>
                    setThreshold(
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                  className="w-24 rounded-md border bg-background px-3 py-1.5 text-sm"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </div>
          </>
        ) : null}

        {error ? (
          <Card className="border-destructive/50 p-3 text-sm text-destructive">
            {error}
          </Card>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onBack} disabled={busy}>
            Change something
          </Button>
          <Button
            onClick={() =>
              onSave(
                isZScore || threshold === null ? undefined : threshold,
                isZScore ? undefined : timeframe
              )
            }
            disabled={busy || (!isZScore && !threshold)}
          >
            {busy ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
            Save alert
          </Button>
        </div>
      </div>
    </>
  );
}
